import ViewModel from "../ViewModel";
import FormValidator from "../../../util/FormValidator";
import AuthRepository from "../../../domain/repositories/auth/AuthRepository";
import ChangeListener from "../../../util/ChangeListener";
import BrowserHistoryHelper from "../../../util/BrowserHistoryHelper";
import BaseView from "../../view/BaseView";

export default class AuthViewModelImpl extends ViewModel {
  public emailQuery: string;
  public passwordQuery: string;
  public isResetHasBeenSend: boolean;

  public isEmailError: boolean;
  public isPasswordError: boolean;
  public loginError: string;
  public forgotError: string;

  public isLoading: boolean;

  private authRepository: AuthRepository;
  private authListener: ChangeListener;

  public constructor(authRepository: AuthRepository) {
    super();

    this.authRepository = authRepository;

    this.emailQuery = "";
    this.passwordQuery = "";
    this.isResetHasBeenSend = false;

    this.isEmailError = false;
    this.isPasswordError = false;
    this.loginError = "";
    this.forgotError = "";

    this.isLoading = false;

    this.authListener = {
      onChanged: (): void => {
        if (authRepository.isAuthorized()) {
          if (authRepository.getUserRole() === "admin") {
            BrowserHistoryHelper.moveTo("/admin");
          } else {
            BrowserHistoryHelper.moveTo("/main");
          }
        }
      },
    };
  }

  public attachView = (baseView: BaseView): void => {
    super.attachView(baseView);
    this.authRepository.addAuthListener(this.authListener);
  };

  public detachView = (baseView: BaseView): void => {
    super.detachView(baseView);
    this.authRepository.removeAuthListener(this.authListener);
  };

  public onEmailQueryChanged = (emailQuery: string): void => {
    this.emailQuery = emailQuery;
    this.isEmailError = false;
    super.notifyViewAboutChanges();
  };

  public onPasswordQueryChanged = (passwordQuery: string): void => {
    this.passwordQuery = passwordQuery;
    this.isPasswordError = false;
    super.notifyViewAboutChanges();
  };

  public onSignIn = async (): Promise<void> => {
    if (this.validateLoginFields()) {
      this.loginError = "";
      this.isLoading = true;
      super.notifyViewAboutChanges();

      try {
        await this.authRepository.signIn(this.emailQuery, this.passwordQuery);
      } catch (e) {
        if (e instanceof Error) {
          this.loginError = e.message;
        } else {
          this.loginError = String(e); // Handle other types of errors
        }
      }
    }

    this.isLoading = false;
    super.notifyViewAboutChanges();
  };

  public onSendResetCode = async (): Promise<void> => {
    if (this.validateEmailField()) {
      this.forgotError = "";
      this.isLoading = true;
      super.notifyViewAboutChanges();

      try {
        await this.authRepository.sendRestorePassword(this.emailQuery);
        this.isResetHasBeenSend = true;
      } catch (e) {
        if (e instanceof Error) {
          this.forgotError = e.message;
        } else {
          this.forgotError = String(e); // Handle other types of errors
        }
      }
    }

    this.isLoading = false;
    super.notifyViewAboutChanges();
  };

  public onForgotAnotherEmail = (): void => {
    this.isResetHasBeenSend = false;
    super.notifyViewAboutChanges();
  };

  private validateLoginFields = (): boolean => {
    if (!this.validateEmailField()) {
      return false;
    }

    if (!this.passwordQuery) {
      this.isPasswordError = true;
      return false;
    }
    this.isPasswordError = false;

    return true;
  };

  private validateEmailField = (): boolean => {
    if (!this.emailQuery || !FormValidator.isValidEmail(this.emailQuery)) {
      this.isEmailError = true;
      return false;
    }
    this.isEmailError = false;

    return true;
  };
}
